import { User } from "@propelauth/javascript";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

type MeAnalyticsProperties = {
  username?: string;
  email?: string;
  company_name?: string;
  company_short_name?: string | null;
  company_id?: string | null;
  team_id?: string | null;
  team_name?: string | null;
  division_id?: string | null;
  division_name?: string | null;
  first_name?: string;
  last_name?: string;
  is_ping_employee?: boolean;
};

type CaptureShareableMapLinkOpenedArgs = {
  readOnlyToken?: string;
  shareableLinkAnalytics?: Record<string, unknown> | null;
  user?: User;
  me?: MeAnalyticsProperties | null;
};

const ANALYTICS_HASH_NAMESPACE = "posthog-shareable-map-v1";
const REDACTED_QUERY_VALUE = "[redacted]";
const SENSITIVE_QUERY_PARAM_KEYS = new Set([
  "access",
  "access_token",
  "auth_token",
  "signature",
  "token",
  "x-ping-signature",
]);
const IDENTIFYING_QUERY_PARAM_KEYS = new Set(["itemkey", "item_key", "sovid"]);

const IS_LOCAL =
  import.meta.env?.VITE_APP_ENV === "local" ||
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.includes("local.pingintel.com");

const omitEmptyProperties = (properties: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

const PRIVATE_ME_ANALYTICS_PROPERTIES = new Set([
  "posthog_guid",
  "analytics_consent",
  "analytics_consent_version",
  "analytics_consent_updated_at",
]);

const omitPrivateMeAnalyticsProperties = (me: object) =>
  Object.fromEntries(
    Object.entries(me).filter(
      ([key]) => !PRIVATE_ME_ANALYTICS_PROPERTIES.has(key),
    ),
  );

const getAnonymousId = () => {
  let anonymousId = localStorage.getItem("anonymous_user_id");
  if (!anonymousId) {
    anonymousId = `anonymous_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("anonymous_user_id", anonymousId);
  }
  return anonymousId;
};

const fallbackHashString = (value: string): string => {
  let hash1 = 0xdeadbeef;
  let hash2 = 0x41c6ce57;

  for (let i = 0; i < value.length; i += 1) {
    const char = value.charCodeAt(i);
    hash1 = Math.imul(hash1 ^ char, 2654435761);
    hash2 = Math.imul(hash2 ^ char, 1597334677);
  }

  hash1 =
    Math.imul(hash1 ^ (hash1 >>> 16), 2246822507) ^
    Math.imul(hash2 ^ (hash2 >>> 13), 3266489909);
  hash2 =
    Math.imul(hash2 ^ (hash2 >>> 16), 2246822507) ^
    Math.imul(hash1 ^ (hash1 >>> 13), 3266489909);

  return `${(hash2 >>> 0).toString(16).padStart(8, "0")}${(hash1 >>> 0).toString(16).padStart(8, "0")}`;
};

const hashString = async (
  value: string | number | null | undefined,
): Promise<string | undefined> => {
  if (value === undefined || value === null || value === "") return undefined;

  const stringValue = String(value);
  try {
    const digest = await window.crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(stringValue),
    );
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch (error) {
    return `fallback_${fallbackHashString(stringValue)}`;
  }
};

const hashForAnalytics = (
  value: string | number | null | undefined,
  label: string,
) =>
  value === undefined || value === null || value === ""
    ? Promise.resolve(undefined)
    : hashString(`${ANALYTICS_HASH_NAMESPACE}:${label}:${value}`);

const sanitizeQueryParamValue = (key: string, value: string) => {
  const normalizedKey = key.toLowerCase();
  if (
    SENSITIVE_QUERY_PARAM_KEYS.has(normalizedKey) ||
    IDENTIFYING_QUERY_PARAM_KEYS.has(normalizedKey)
  ) {
    return REDACTED_QUERY_VALUE;
  }
  return value;
};

const getSanitizedSearch = (): string => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const sanitizedParams = new URLSearchParams();

    for (const [key, value] of searchParams.entries()) {
      sanitizedParams.append(key, sanitizeQueryParamValue(key, value));
    }

    const sanitizedSearch = sanitizedParams.toString();
    return sanitizedSearch ? `?${sanitizedSearch}` : "";
  } catch (error) {
    return "";
  }
};

const getRedactedPathname = (pathname = window.location.pathname): string => {
  try {
    return pathname
      .split("/")
      .map((segment) => (segment.startsWith("s-") ? ":sovid" : segment))
      .join("/");
  } catch (error) {
    return pathname;
  }
};

const getRedactedCurrentUrl = (): string => {
  try {
    const url = new URL(window.location.href);
    url.pathname = getRedactedPathname(url.pathname);

    for (const key of Array.from(url.searchParams.keys())) {
      const value = url.searchParams.get(key) ?? "";
      url.searchParams.set(key, sanitizeQueryParamValue(key, value));
    }

    return url.toString();
  } catch (error) {
    return `${getRedactedPathname()}${getSanitizedSearch()}`;
  }
};

const parseSearchParams = (): Record<string, string> => {
  try {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = sanitizeQueryParamValue(key, value);
    }

    return params;
  } catch (error) {
    return {};
  }
};

type ShareableTokenPayload = {
  aud?: string | number | null;
  cid?: string | number | null;
  did?: string | number | null;
  exp?: number;
  iat?: number;
  sub?: string;
  ver?: number;
};

const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binaryString = window.atob(paddedBase64);
  const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const decodeShareableTokenPayload = (
  token?: string,
): ShareableTokenPayload | null => {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;
    return JSON.parse(decodeBase64Url(payload)) as ShareableTokenPayload;
  } catch (error) {
    return null;
  }
};

const getNormalizedCurrentUrlForHash = (): string => {
  const url = new URL(window.location.href);
  const sortedEntries = Array.from(url.searchParams.entries()).sort(
    ([keyA, valueA], [keyB, valueB]) =>
      `${keyA}=${valueA}`.localeCompare(`${keyB}=${valueB}`),
  );

  url.hash = "";
  url.search = "";
  sortedEntries.forEach(([key, value]) => url.searchParams.append(key, value));
  return url.toString();
};

const parseCenter = (center: string | null): { lat?: number; lng?: number } => {
  if (!center) return {};

  const [rawLat, rawLng] = center.split(",");
  const lat = Number(rawLat);
  const lng = Number(rawLng);

  return {
    lat: Number.isFinite(lat) ? lat : undefined,
    lng: Number.isFinite(lng) ? lng : undefined,
  };
};

const getSovidFromPath = (): string | undefined =>
  window.location.pathname
    .split("/")
    .find((segment) => segment.startsWith("s-"));

const getShareableMapLinkProperties = async (
  readOnlyToken?: string,
): Promise<Record<string, unknown>> => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = readOnlyToken || searchParams.get("token") || undefined;
  if (!token) return {};

  const tokenPayload = decodeShareableTokenPayload(token);
  const itemKey =
    searchParams.get("itemKey") || searchParams.get("item_key") || undefined;
  const center = parseCenter(searchParams.get("center"));
  const zoom = Number(searchParams.get("zoom"));
  const sovid =
    tokenPayload?.sub || searchParams.get("sovid") || getSovidFromPath();

  const [
    shareableTokenId,
    shareableUrlId,
    shareableItemKeyHash,
    shareableSovidHash,
    tokenAudienceTeamIdHash,
    tokenCompanyIdHash,
    tokenDivisionIdHash,
  ] = await Promise.all([
    hashForAnalytics(token, "token"),
    hashForAnalytics(getNormalizedCurrentUrlForHash(), "url"),
    hashForAnalytics(itemKey, "item_key"),
    hashForAnalytics(sovid, "sovid"),
    hashForAnalytics(tokenPayload?.aud, "team_id"),
    hashForAnalytics(tokenPayload?.cid, "company_id"),
    hashForAnalytics(tokenPayload?.did, "division_id"),
  ]);

  return omitEmptyProperties({
    is_shareable_map_link: true,
    page_url: getRedactedCurrentUrl(),
    page_pathname: getRedactedPathname(),
    pathname: getRedactedPathname(),
    search: getSanitizedSearch(),
    shareable_token_id: shareableTokenId,
    shareable_url_id: shareableUrlId,
    shareable_item_key_hash: shareableItemKeyHash,
    shareable_sovid_hash: shareableSovidHash,
    shareable_token_audience_team_id_hash: tokenAudienceTeamIdHash,
    shareable_token_company_id_hash: tokenCompanyIdHash,
    shareable_token_division_id_hash: tokenDivisionIdHash,
    shareable_token_version: tokenPayload?.ver,
    shareable_token_issued_at: tokenPayload?.iat,
    shareable_token_expires_at: tokenPayload?.exp,
    shareable_center_lat: center.lat,
    shareable_center_lng: center.lng,
    shareable_zoom: Number.isFinite(zoom) ? zoom : undefined,
    shareable_has_item_key: !!itemKey,
  });
};

const getEmailDomain = (email?: string) =>
  email?.includes("@") ? email.split("@")[1] : undefined;

const getViewerAnalyticsProperties = (
  user?: User,
  me?: MeAnalyticsProperties | null,
) => {
  const emailDomain = getEmailDomain(user?.email || me?.email);
  const viewerGroupId =
    me?.team_id || me?.division_id || me?.company_id || emailDomain;

  return omitEmptyProperties({
    viewer_user_type: user ? "authenticated" : "readonly",
    viewer_propelauth_user_id: user?.userId,
    viewer_email_domain: emailDomain,
    viewer_company_id: me?.company_id,
    viewer_company_short_name: me?.company_short_name,
    viewer_company_name: me?.company_name,
    viewer_division_id: me?.division_id,
    viewer_division_name: me?.division_name,
    viewer_team_id: me?.team_id,
    viewer_team_name: me?.team_name,
    viewer_group_id: viewerGroupId,
    viewer_group_type: viewerGroupId
      ? me?.team_id
        ? "team"
        : me?.division_id
          ? "division"
          : me?.company_id
            ? "company"
            : "email_domain"
      : undefined,
    viewer_is_ping_employee: me?.is_ping_employee,
  });
};

export const useAnalytics = () => {
  const posthog = usePostHog();

  const identifyPostHogUser = useCallback(
    (
      user: User,
      me: {
        username: string;
        email: string;
        company_name: string;
        team_name: string;
        division_name: string;
        first_name: string;
        last_name: string;
      },
      distinctId = user.email,
    ) => {
      const environment = import.meta.env?.VITE_APP_ENV;
      console.assert(environment, "VITE_APP_ENV is not set");

      if (!user || !user.userId || !user.email || !distinctId) return;
      if (!posthog?.has_opted_in_capturing()) return;

      const meProperties = omitPrivateMeAnalyticsProperties(me);
      const userProperties = {
        user_type: "authenticated",
        user_id: user.userId,
        propelauth_user_id: user.userId,
        name: `${user.firstName} ${user.lastName}`.trim() || user.email,
        email_domain: user.email.split("@")[1],
        email_confirmed: user.emailConfirmed,
        has_password: user.hasPassword,
        mfa_enabled: user.mfaEnabled,
        can_create_orgs: user.canCreateOrgs,
        account_locked: user.locked,
        account_enabled: user.enabled,
        is_ping_employee: user.email.includes("@pingintel.com"),
        created_at: new Date(user.createdAt * 1000).toISOString(),
        last_active_at: new Date(user.lastActiveAt * 1000).toISOString(),
        is_pli: window.location.href?.includes("pli"),
        is_readonly_url: window.location.href?.includes("token="),
        search: getSanitizedSearch(),
        pathname: getRedactedPathname(),
        hostname: window.location.hostname,
        environment,
        ...meProperties,
        ...parseSearchParams(),
      };

      try {
        posthog.identify(distinctId, userProperties);
      } catch (error) {
        console.error("Error identifying PostHog user:", error);
      }
    },
    [posthog],
  );

  const identifyReadonlyUser = useCallback(() => {
    const environment = import.meta.env?.VITE_APP_ENV;
    console.assert(environment, "VITE_APP_ENV is not set");

    const anonymousId = getAnonymousId();
    const userProperties = {
      user_type: "readonly",
      search: getSanitizedSearch(),
      pathname: getRedactedPathname(),
      hostname: window.location.hostname,
      environment,
      is_anonymous: true,
      is_pli: window.location.href?.includes("pli"),
      is_readonly_url: window.location.href?.includes("token="),
      ...parseSearchParams(),
    };

    if (IS_LOCAL) {
      console.info(
        "[PostHog] identify readonly (local):",
        anonymousId,
        userProperties,
      );
      return;
    }

    try {
      posthog?.identify(anonymousId, userProperties);
    } catch (error) {
      console.error("Error identifying PostHog readonly user:", error);
    }
  }, [posthog]);

  const captureEvent = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      const enrichedProperties = {
        page_url: getRedactedPathname(),
        page_title: document.title,
        referrer: document.referrer,
        environment: import.meta.env?.VITE_APP_ENV,
        timestamp: new Date().toISOString(),
        is_pli: window.location.href?.includes("pli"),
        is_readonly_url: window.location.href?.includes("token="),
        search: getSanitizedSearch(),
        pathname: getRedactedPathname(),
        hostname: window.location.hostname,
        ...parseSearchParams(),
        ...properties,
      };

      try {
        posthog?.capture(eventName, enrichedProperties);
      } catch (error) {
        console.error("Error capturing PostHog event:", error);
      }
    },
    [posthog],
  );

  const captureShareableMapLinkOpened = useCallback(
    async ({
      readOnlyToken,
      shareableLinkAnalytics,
      user,
      me,
    }: CaptureShareableMapLinkOpenedArgs) => {
      const linkProperties = await getShareableMapLinkProperties(readOnlyToken);
      if (!linkProperties.shareable_token_id) return;

      const shareableTokenId = String(linkProperties.shareable_token_id);
      const shareableUrlId = linkProperties.shareable_url_id
        ? String(linkProperties.shareable_url_id)
        : undefined;
      const viewerProperties = getViewerAnalyticsProperties(user, me);
      const groupProperties = omitEmptyProperties({
        ...linkProperties,
        ...shareableLinkAnalytics,
      });

      if (!IS_LOCAL) {
        try {
          posthog?.group(
            "shareable_map_link",
            shareableTokenId,
            groupProperties,
          );
          if (shareableUrlId) {
            posthog?.group(
              "shareable_map_url",
              shareableUrlId,
              groupProperties,
            );
          }
        } catch (error) {
          console.error(
            "Error registering PostHog shareable map groups:",
            error,
          );
        }
      }

      captureEvent(
        POSTHOG_EVENTS.SHAREABLE_MAP_LINK_OPENED,
        omitEmptyProperties({
          ...linkProperties,
          ...shareableLinkAnalytics,
          ...viewerProperties,
          $groups: omitEmptyProperties({
            shareable_map_link: shareableTokenId,
            shareable_map_url: shareableUrlId,
          }),
        }),
      );
    },
    [posthog, captureEvent],
  );

  const resetPostHogUser = useCallback(() => {
    if (IS_LOCAL) {
      console.info("[PostHog] reset (local)");
      return;
    }
    try {
      posthog?.reset();
    } catch (error) {
      console.error("Error resetting PostHog user:", error);
    }
  }, [posthog]);

  const syncPostHogIdentity = useCallback(
    (
      user: Parameters<typeof identifyPostHogUser>[0],
      me: Parameters<typeof identifyPostHogUser>[1],
    ) => {
      const currentDistinctId = posthog?.get_distinct_id();
      if (currentDistinctId === user.email) return;
      identifyPostHogUser(user, me);
    },
    [posthog, identifyPostHogUser],
  );

  return {
    identifyPostHogUser,
    identifyReadonlyUser,
    captureEvent,
    captureShareableMapLinkOpened,
    resetPostHogUser,
    syncPostHogIdentity,
  };
};

export const POSTHOG_EVENTS = {
  PING_DATA_UPGRADE_STARTED: "ping_data_upgrade_started",
  PING_DATA_UPGRADE_COMPLETED: "ping_data_upgrade_completed",
  PING_DATA_PLAN_DOWNGRADED: "ping_data_plan_downgraded",
  PING_DATA_ACCOUNT_DELETED: "ping_data_account_deleted",
  PLI_REPORT_GENERATION_COMPLETED: "pli_report_generation_completed",
  MAPS_REPORT_GENERATION_COMPLETED: "maps_report_generation_completed",
  PLI_REPORT_GENERATION_FAILED: "pli_report_generation_failed",
  MAPS_REPORT_GENERATION_FAILED: "maps_report_generation_failed",
  OVERLAY_SELECTED: "overlay_selected",
  PLI_PIN_MOVED: "pli_pin_moved",
  MAPS_PING_MOVED_GEOCODING_MODE: "maps_ping_moved_geocoding_mode",
  PLI_ADDRESS_SEARCHED: "pli_address_searched",
  THIRD_PARTY_DATA_PULLED: "third_party_data_pulled",
  MARKETING_SOV_GENERATION: "marketing_sov_generation_used",
  MEASUREMENT_TOOL: "measurement_tool",
  ENRICH_BUTTON_CLICKED: "enrich_button_clicked",
  OUTPUT_DOWNLOADED: "output_downloaded",
  FILE_DOWNLOAD: "file_download",
  FILE_PREVIEW: "file_preview",
  FILE_PREVIEW_FULLSCREEN: "file_preview_fullscreen",
  SHAREABLE_MAP_LINK_OPENED: "shareable_map_link_opened",
  DOCUMENT_UPLOADED: "vision_document_uploaded",
  PORTAL_TEAM_SELECTED: "portal_team_selected",
  PORTAL_SUBMISSION_STARTED: "portal_submission_started",
  PORTAL_FILES_ADDED: "portal_files_added",
  PORTAL_FILE_REMOVED: "portal_file_removed",
  PORTAL_SUBMISSION_SUBMITTED: "portal_submission_submitted",
  PORTAL_SUBMISSION_SUCCEEDED: "portal_submission_succeeded",
  PORTAL_SUBMISSION_FAILED: "portal_submission_failed",
  PORTAL_NEW_SUBMISSION_CLICKED: "portal_new_submission_clicked",
  PORTAL_LOGOUT_CLICKED: "portal_logout_clicked",
  ORG_CHECK_RESULT: "org_check_result",
  DOCUMENT_GENERATED: "submission_package_generated",
  DATA_RELEVANCE_FILTER_TOGGLED: "data_relevance_filter_toggled",
  DATA_RELEVANCE_FILTERS_CLEARED: "data_relevance_filters_cleared",
  MAPS_VIEW_CHANGED: "maps_view_changed",
  OVERVIEW_SCROLL_DEPTH: "overview_scroll_depth",
};
